/**
 * Renders a live DOM element (with CSS animations) onto a canvas each frame.
 *
 * Pipeline per frame:
 *  1. Deep-clone the source element
 *  2. Discover animated properties via `Element.getAnimations()`
 *  3. Read current values with `getComputedStyle()` and bake them as inline styles
 *  4. Serialize the frozen clone to XHTML via `XMLSerializer`
 *  5. Wrap in SVG `<foreignObject>`, load as `<img>` via data URI, `drawImage()` to canvas
 *
 * The SVG viewport always matches the source element's layout size (from
 * `getBoundingClientRect`) so that HTML reflows identically. Scaling to a
 * different size on the canvas is handled by `drawImage`.
 *
 * Data URIs are used instead of blob URLs because all browsers agree not to
 * taint the canvas for foreignObject SVGs loaded via `data:` URIs. This keeps
 * the canvas origin-clean, allowing `getImageData()`, `toBlob()`, and
 * `new VideoFrame(canvas)` to work — critical for the video encoding pipeline.
 *
 * `createImageBitmap` cannot be used — Chrome rejects foreignObject SVGs in that API.
 */
export class DomToCanvasRenderer {
    xmlSerializer = new XMLSerializer();
    svgImg = new Image();
    cssCache = null;
    /**
     * Snapshot a live DOM element into an SVG string with all CSS animations
     * frozen at their current computed values.
     *
     * The returned string is a self-contained SVG document suitable for loading
     * into an `<img>` element. The page's inline `<style>` blocks are embedded
     * so that non-animated styles and pseudo-elements render correctly.
     *
     * @param sourceEl - The live DOM element to snapshot (must be in the document)
     * @param width    - SVG viewport width in pixels (use the source element's layout width)
     * @param height   - SVG viewport height in pixels (use the source element's layout height)
     * @param css      - Optional CSS to embed. When omitted, all inline `<style>`
     *                   blocks from the current document are collected automatically.
     * @returns A complete SVG markup string
     */
    snapshotToSvg(sourceEl, width, height, css) {
        const clone = sourceEl.cloneNode(true);
        for (const s of clone.querySelectorAll('script'))
            s.remove();
        this.freezeAnimations(sourceEl, clone);
        const srcAll = sourceEl.querySelectorAll('*');
        const clnAll = clone.querySelectorAll('*');
        for (let i = 0; i < srcAll.length; i++) {
            this.freezeAnimations(srcAll[i], clnAll[i]);
        }
        const xhtml = this.xmlSerializer.serializeToString(clone);
        const embeddedCss = css ?? this.collectCSS();
        // Animations and transitions must not play inside the snapshot:
        // the SVG image renders them at time 0, so an element carrying an
        // animation class whose first keyframe is invisible (a common
        // "pop in" pattern) would vanish whenever the animation is not
        // currently running — running animations are already baked as
        // inline styles by freezeAnimations, which win over the shorthand.
        const animationFreeze = '\n*{animation:none !important;transition:none !important;}';
        return (`<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}">` +
            `<foreignObject width="100%" height="100%">` +
            `<div xmlns="http://www.w3.org/1999/xhtml"` +
            ` style="position:relative;width:${width}px;height:${height}px;overflow:hidden">` +
            `<style><![CDATA[${embeddedCss}${animationFreeze}]]></style>` +
            xhtml +
            '</div></foreignObject></svg>');
    }
    /**
     * Render a live DOM element onto a canvas 2D context in a single async step.
     * Accepts both regular `CanvasRenderingContext2D` and `OffscreenCanvasRenderingContext2D`.
     *
     * The SVG is always generated at the source element's layout size (from
     * `getBoundingClientRect`) so that text, flexbox, and other layout features
     * render identically to what's on screen. If `dest` specifies a different
     * size, `drawImage` scales the result; if `dest` is omitted the image fills
     * the entire canvas.
     *
     * @param sourceEl - The live DOM element to capture
     * @param ctx      - The 2D canvas context to draw into (regular or offscreen)
     * @param dest     - Optional destination rectangle on the canvas
     * @param css      - Optional CSS override (see {@link snapshotToSvg})
     * @returns A promise that resolves with the drawImage time in ms
     */
    renderToCanvas(sourceEl, ctx, dest, css) {
        const rect = sourceEl.getBoundingClientRect();
        const svgW = Math.round(rect.width);
        const svgH = Math.round(rect.height);
        const svg = this.snapshotToSvg(sourceEl, svgW, svgH, css);
        const url = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg);
        const dx = dest?.x ?? 0;
        const dy = dest?.y ?? 0;
        const dw = dest?.width ?? ctx.canvas.width;
        const dh = dest?.height ?? ctx.canvas.height;
        return new Promise((resolve, reject) => {
            this.svgImg.onload = () => {
                const t0 = performance.now();
                ctx.drawImage(this.svgImg, dx, dy, dw, dh);
                const drawMs = performance.now() - t0;
                resolve(drawMs);
            };
            this.svgImg.onerror = (e) => {
                reject(new Error(`SVG image load failed: ${e}`));
            };
            this.svgImg.src = url;
        });
    }
    /**
     * Clear the CSS cache. Call this when the page's `<style>` blocks change
     * and the auto-collected CSS should be refreshed.
     */
    invalidateCSSCache() {
        this.cssCache = null;
    }
    /**
     * Release resources. Call when the renderer is no longer needed.
     */
    dispose() {
        this.svgImg.onload = null;
        this.svgImg.onerror = null;
        this.cssCache = null;
    }
    // ── internals ────────────────────────────────────────────────────
    collectCSS() {
        if (this.cssCache !== null)
            return this.cssCache;
        const parts = [];
        const sheets = document.querySelectorAll('style');
        for (let i = 0; i < sheets.length; i++) {
            parts.push(sheets[i].textContent ?? '');
        }
        this.cssCache = parts.join('\n');
        return this.cssCache;
    }
    /** Metadata keys that `getKeyframes()` returns alongside CSS property values. */
    static KF_META = {
        offset: 1,
        easing: 1,
        composite: 1,
        computedOffset: 1,
    };
    /**
     * Discover which CSS properties are currently being animated on `el`
     * via the Web Animations API, returning their camelCase names.
     */
    getAnimatedProps(el) {
        const anims = el.getAnimations();
        if (!anims.length)
            return null;
        const set = {};
        for (const anim of anims) {
            const effect = anim.effect;
            if (!effect?.getKeyframes)
                continue;
            for (const kf of effect.getKeyframes()) {
                for (const key of Object.keys(kf)) {
                    if (!DomToCanvasRenderer.KF_META[key])
                        set[key] = 1;
                }
            }
        }
        const out = Object.keys(set);
        return out.length ? out : null;
    }
    static camelToDash(s) {
        return s.replace(/[A-Z]/g, (c) => '-' + c.toLowerCase());
    }
    /**
     * Read the current computed values of all animated properties on `srcEl`
     * and bake them as `!important` inline styles on `clnEl`, then disable
     * the animation so the SVG renderer sees the frozen values.
     */
    freezeAnimations(srcEl, clnEl) {
        const props = this.getAnimatedProps(srcEl);
        if (!props)
            return;
        const cs = getComputedStyle(srcEl);
        for (const prop of props) {
            const dash = DomToCanvasRenderer.camelToDash(prop);
            clnEl.style.setProperty(dash, cs.getPropertyValue(dash), 'important');
        }
        clnEl.style.setProperty('animation', 'none', 'important');
    }
}
